/**
 * Iter-185.BJ — Single source of truth for funnel CTA vocabulary.
 *
 * Locks the 4 user-preferred CTA labels (iter-185.BI close-out) at
 * the type-system level so every surface — homepage hero, intake
 * modals, sticky bar, exit-intent modal, savings calculator, lender
 * table, success-page CTAs — imports from ONE place. Any future
 * vocabulary drift is caught at import time, not by regex regression.
 *
 * Why constants instead of an env-var or DB-backed config:
 *   • These are UI strings shipped with the bundle — fetching them
 *     from an API would block first paint.
 *   • The admin Funnel Copy panel ALREADY allows per-site edits via
 *     MongoDB `funnel_copy.primary_cta_text` overrides. These
 *     constants are the build-time defaults that ship with every
 *     bundle (and the values seeded into Mongo on a fresh install).
 *   • Type-system constants give us go-to-definition + IDE refactor
 *     support that string literals can't.
 *
 * Updating the canon: change the literal here AND update the matching
 * test_iter185_bj_cta_vocabulary_single_source_of_truth.py spec — the
 * test pins the canonical values so a partial rename gets caught.
 */

export const CTA_INSURANCE = "Compare Insurance Quotes";
export const CTA_AUTO_LOAN = "Find Loan Offers";
export const CTA_REFINANCE = "Lower My Payment";
export const CTA_WARRANTY = "Compare Protection Plans";

/**
 * Iter-190.G — Cross-vertical aggregator CTA. The homepage Live
 * Estimate calculator blends insurance + auto-loan + refinance
 * savings into a single number, so its CTA must be category-agnostic
 * (showing "Compare Insurance Quotes" when the user is actually
 * looking at blended numbers misrepresents what clicking the button
 * does — the modal that opens routes to whichever vertical has the
 * higher savings share, which can flip mid-session as the user
 * drags sliders). "Compare Offers" honestly describes the action
 * across both lanes.
 */
export const CTA_SAVINGS_CALCULATOR = "Compare Offers";

/**
 * Convenience map for surfaces that resolve a CTA from a category
 * key (e.g. `MobileStickyBar.resolveSticky()` returns the category
 * key as a discriminator). Auto-loan-new and auto-loan-used both
 * collapse to the single "Find Loan Offers" verb per iter-185.BI.
 */
export const CTA_BY_CATEGORY = {
  insurance: CTA_INSURANCE,
  "auto-loan": CTA_AUTO_LOAN,
  "auto-loan-new": CTA_AUTO_LOAN,
  "auto-loan-used": CTA_AUTO_LOAN,
  refinance: CTA_REFINANCE,
  "loan-refinance": CTA_REFINANCE,
  warranty: CTA_WARRANTY,
};

/**
 * Default fallback for surfaces with no category context (e.g. the
 * generic ExitIntentModal on the homepage / content surfaces). The
 * insurance verb wins because (1) insurance is the highest-traffic
 * vertical and (2) "Compare Insurance Quotes" is the most concrete
 * + benefit-driven verb of the four.
 */
export const CTA_DEFAULT = CTA_INSURANCE;
